import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Activity, Loader2, Trash2, AlertTriangle, CheckCircle, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminHealthTab = ({ token }) => {
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [removingKey, setRemovingKey] = useState(null);
  const [showBots, setShowBots] = useState(false);

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API_URL}/api/portfolio/admin/health-scan`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Scan failed');
      setResults(data);
      if (data.total_issues === 0) toast.success('All portfolios healthy!');
      else toast.warning(`Found ${data.total_issues} issues (${data.real_user_issues} real users)`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setScanning(false);
    }
  };

  const removeHolding = async (userId, symbol) => {
    const key = `${userId}-${symbol}`;
    if (!window.confirm(`Remove ${symbol} from this user and refund original cost?`)) return;
    setRemovingKey(key);
    try {
      const res = await fetch(`${API_URL}/api/portfolio/admin/remove-holding?target_user_id=${userId}&symbol=${symbol}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Removed ${symbol} — refunded V$${data.refund_amount?.toLocaleString()}`);
      setResults(prev => prev ? {
        ...prev,
        issues: prev.issues.filter(i => !(i.user_id === userId && i.symbol === symbol)),
        total_issues: prev.total_issues - 1
      } : null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRemovingKey(null);
    }
  };

  const resetPortfolio = async (userId, username) => {
    if (!window.confirm(`Reset ${username}'s entire portfolio to V$100,000?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/portfolio/reset?target_user_id=${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`${username}'s portfolio reset`);
      setResults(prev => prev ? {
        ...prev,
        issues: prev.issues.filter(i => i.user_id !== userId),
        total_issues: prev.total_issues - prev.issues.filter(i => i.user_id === userId).length
      } : null);
    } catch {
      toast.error('Failed to reset portfolio');
    }
  };

  const issueLabel = (type) => {
    const labels = {
      zero_buy_price: { text: 'Zero Buy Price', color: 'bg-red-500/20 text-red-400' },
      extreme_change: { text: '>500% Change', color: 'bg-amber-500/20 text-amber-400' },
      extreme_value: { text: 'Value >V$500K', color: 'bg-orange-500/20 text-orange-400' },
      portfolio_over_500k: { text: 'Portfolio >V$500K', color: 'bg-red-500/20 text-red-400' }
    };
    return labels[type] || { text: type, color: 'bg-slate-500/20 text-slate-400' };
  };

  const filteredIssues = results?.issues?.filter(i => showBots || !i.is_bot) || [];

  return (
    <div className="space-y-6">
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-emerald-400" />
              Portfolio Health Scanner
            </span>
            <Button
              onClick={runScan}
              disabled={scanning}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="run-health-scan-btn"
            >
              {scanning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {scanning ? 'Scanning...' : 'Run Scan'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 text-sm">
            Scans all portfolios for: zero buy prices, extreme gains/losses (&gt;500%), and portfolios valued above V$500K.
          </p>

          {results && (
            <div className="mt-4 space-y-4">
              {/* Summary */}
              <div className="flex gap-3 flex-wrap">
                <div className={`px-4 py-2 rounded-lg ${results.real_user_issues === 0 ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  {results.real_user_issues === 0
                    ? <span className="flex items-center gap-2 text-emerald-400"><CheckCircle className="h-4 w-4" /> Real Users: Clean</span>
                    : <span className="flex items-center gap-2 text-red-400"><AlertTriangle className="h-4 w-4" /> Real Users: {results.real_user_issues} issues</span>
                  }
                </div>
                <div className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700">
                  <span className="text-slate-400">Bot Issues: {results.bot_issues}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBots(!showBots)}
                  className="border-slate-700 text-slate-400"
                >
                  {showBots ? 'Hide Bots' : 'Show Bots'}
                </Button>
              </div>

              {/* Issues List */}
              {filteredIssues.length > 0 ? (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filteredIssues.map((issue, idx) => (
                    <div key={`${issue.user_id}-${issue.symbol}-${idx}`} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium text-sm">{issue.username}</span>
                          {issue.is_bot && <Badge className="bg-slate-500/20 text-slate-400 text-[10px]">Bot</Badge>}
                          <span className="text-slate-500 text-xs">|</span>
                          <span className="text-emerald-400 font-medium text-sm">{issue.symbol}</span>
                          {issue.issue_types.map(t => {
                            const l = issueLabel(t);
                            return <Badge key={t} className={`${l.color} text-[10px] px-1.5 py-0`}>{l.text}</Badge>;
                          })}
                        </div>
                        <div className="flex gap-4 mt-1 text-xs text-slate-500">
                          {issue.symbol !== 'PORTFOLIO' && (
                            <>
                              <span>Buy: V${issue.buy_price_vd?.toFixed(4)}</span>
                              <span>Live: V${issue.live_price?.toFixed(4)}</span>
                              <span>Units: {issue.units}</span>
                            </>
                          )}
                          <span className="text-white">Value: V${issue.holding_value?.toLocaleString()}</span>
                          {issue.pct_change !== 0 && (
                            <span className={issue.pct_change >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                              {issue.pct_change > 0 ? '+' : ''}{issue.pct_change}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        {issue.symbol !== 'PORTFOLIO' ? (
                          <Button
                            onClick={() => removeHolding(issue.user_id, issue.symbol)}
                            disabled={removingKey === `${issue.user_id}-${issue.symbol}`}
                            variant="ghost"
                            size="sm"
                            className="h-8 text-red-400 hover:bg-red-500/10 text-xs"
                            data-testid={`remove-${issue.user_id}-${issue.symbol}`}
                          >
                            {removingKey === `${issue.user_id}-${issue.symbol}`
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <><Trash2 className="h-3.5 w-3.5 mr-1" />Remove</>
                            }
                          </Button>
                        ) : (
                          <Button
                            onClick={() => resetPortfolio(issue.user_id, issue.username)}
                            variant="ghost"
                            size="sm"
                            className="h-8 text-red-400 hover:bg-red-500/10 text-xs"
                          >
                            <RefreshCw className="h-3.5 w-3.5 mr-1" />Reset
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-3 opacity-50" />
                  <p className="text-slate-400">{showBots ? 'No issues found' : 'No real user issues found'}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
